import type {
  AutoInputSource,
  ChestTrigger,
  HiveStageId,
  CommunityChallenge,
  HiveChestReward,
  HiveCollectible,
  HiveStage,
  LeagueMember,
  ProgressPhoto,
  Recap,
  ReminderFrequency,
  SmartNotificationExample,
} from '../types/engagement'
import { monthRange, monthlyCheckpoints, weekRange, yearRange } from '../utils/challenges'

/** Sample challenge windows follow the current calendar so the prototype always reads as live. */
const thisWeek = weekRange()
const thisMonth = monthRange()
const thisYear = yearRange()
/** Elapsed months this year — sample checkpoint values only fill months already passed. */
const monthsElapsed = new Date().getMonth()
const elapsedSample = (values: number[]) => values.slice(0, monthsElapsed)

/* ------------------------------------------------------------------ *
 * Engagement — APPROVED PRODUCT RULES vs CONFIGURABLE PARAMETERS.
 *
 * Approved rules (fixed): features are optional; smart reminders learn the
 * user's habitual Daily Check time; Streak Protection covers ONE missed day
 * and never counts it as completed; chest rewards are Bee bonuses outside the
 * 100 Bee base, never purchased, no gambling language; leagues are small,
 * optional groups, never global and never ranked on health data; the hive has
 * five visual stages; notification frequency and quiet hours are user-controlled.
 *
 * Everything in `engagementConfig` is a CONFIGURABLE PARAMETER. The values
 * below are prototype placeholders, not approved product decisions. In
 * production they are served by the backend (REQUIRES BACKEND) and editable
 * only by authorized product roles.
 * ------------------------------------------------------------------ */

export type WeekdayId = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export interface EngagementConfig {
  smartReminders: {
    /** Minutes before the learned habitual time. Configurable; 0 = at the habitual time. */
    leadMinutes: number
    /** How many recent check-ins are used to learn the habitual time. */
    learningSampleSize: number
    /** Fallback time when adaptive timing is off or there is not enough history. */
    fallbackTime: string
    /** Max notifications per day for each frequency level, across every category. */
    dailyCap: Record<ReminderFrequency, number>
    defaultQuietHours: { from: string; to: string }
  }
  streakProtection: {
    /** Approved: one protected missed day. */
    maxCharges: 1
    /** Minimum streak length before protection applies. Configurable. */
    minStreakDays: number
    /** Consecutive completed days needed to recharge. Configurable. */
    rechargeDays: number
    /** Whether protection starts ON for new users. Configurable. */
    defaultEnabled: boolean
  }
  hiveChest: {
    /** Max chests in a rolling window. Configurable. */
    maxPerWindow: number
    windowDays: number
    /** Bonus Bee range (outside the 100 Bee base). Configurable. */
    minBonusBee: number
    maxBonusBee: number
    /** Which milestones make a user eligible. Configurable. */
    eligibleTriggers: ChestTrigger[]
    /** Streak length that counts as a `streakMilestone`. Configurable. */
    streakMilestoneDays: number
  }
  league: {
    groupSize: number
    /** Approved constraint: never Life Score, body or health data. */
    scoringRule: 'checkDays' | 'checkDaysAndChallenges'
    /** Approved: the league is weekly — always a seven-day cycle. */
    cycleLengthDays: 7
    resetDay: WeekdayId
    defaultOptIn: false
  }
  hive: {
    /** Bee represented by one filled cell. Configurable. */
    beePerCell: number
    /** Cumulative Bee at which each of the five stages starts. Configurable. */
    stageThresholds: Record<HiveStageId, number>
  }
  /* Recap parameters live in data/recapConfig.ts (single shared recap source). */
}

export const engagementConfig: EngagementConfig = {
  smartReminders: {
    leadMinutes: 30,
    learningSampleSize: 14,
    fallbackTime: '20:00',
    dailyCap: { essential: 1, balanced: 3, all: 6 },
    defaultQuietHours: { from: '22:30', to: '07:00' },
  },
  streakProtection: {
    maxCharges: 1,
    minStreakDays: 3,
    rechargeDays: 7,
    defaultEnabled: true,
  },
  hiveChest: {
    maxPerWindow: 1,
    windowDays: 7,
    minBonusBee: 5,
    maxBonusBee: 15,
    eligibleTriggers: ['streakMilestone', 'perfectDay', 'challengeDone', 'returning'],
    streakMilestoneDays: 7,
  },
  league: {
    groupSize: 7,
    scoringRule: 'checkDays',
    cycleLengthDays: 7,
    resetDay: 'mon',
    defaultOptIn: false,
  },
  hive: {
    beePerCell: 100,
    stageThresholds: { cell: 0, comb: 500, young: 1500, living: 4000, golden: 8000 },
  },
}

/** The user's last 14 Daily Check completion times (prototype sample, HH:MM). */
export const recentCheckTimes = [
  '20:42', '21:05', '20:31', '20:50', '19:58', '20:47', '21:12',
  '20:36', '20:44', '20:29', '20:55', '20:40', '21:01', '20:38',
]

/** Median of HH:MM strings → usual check time. */
export function usualCheckTime(times: string[] = recentCheckTimes): string {
  const minutes = times
    .map((time) => {
      const [h, m] = time.split(':').map(Number)
      return h * 60 + m
    })
    .sort((a, b) => a - b)
  const mid = Math.floor(minutes.length / 2)
  const median =
    minutes.length % 2 === 0 ? Math.round((minutes[mid - 1] + minutes[mid]) / 2) : minutes[mid]
  return toClock(median)
}

export function reminderTime(
  usual: string,
  lead = engagementConfig.smartReminders.leadMinutes
): string {
  const [h, m] = usual.split(':').map(Number)
  return toClock((h * 60 + m - lead + 1440) % 1440)
}

function toClock(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const frequencyRank: Record<ReminderFrequency, number> = { essential: 0, balanced: 1, all: 2 }

export function sendsAt(example: SmartNotificationExample, frequency: ReminderFrequency): boolean {
  return frequencyRank[frequency] >= frequencyRank[example.minFrequency]
}

export const smartNotificationExamples: SmartNotificationExample[] = [
  {
    id: 'daily-check',
    kind: 'dailyCheck',
    minFrequency: 'essential',
    title: { 'pt-BR': 'Seu Daily Check está esperando', 'en-US': 'Your Daily Check is ready' },
    body: {
      'pt-BR': 'Faltam 3 hábitos. Leva menos de um minuto.',
      'en-US': '3 habits left. It takes under a minute.',
    },
    timing: {
      'pt-BR': 'Adaptado ao seu horário habitual',
      'en-US': 'Adapted to your usual time',
    },
  },
  {
    id: 'challenge',
    kind: 'challenge',
    minFrequency: 'balanced',
    title: { 'pt-BR': 'Desafio Corrida · 32 de 40 km', 'en-US': 'Running challenge · 32 of 40 km' },
    body: {
      'pt-BR': 'Você está a 8 km da meta do grupo nesta semana.',
      'en-US': "You're 8 km from this week's group goal.",
    },
    timing: {
      'pt-BR': 'Só quando o progresso muda de forma relevante',
      'en-US': 'Only when progress changes meaningfully',
    },
  },
  {
    id: 'social',
    kind: 'social',
    minFrequency: 'balanced',
    title: { 'pt-BR': 'Lucas publicou no Corrida', 'en-US': 'Lucas posted in Running' },
    body: {
      'pt-BR': 'Novo recorde pessoal nos 10 km.',
      'en-US': 'New 10 km personal best.',
    },
    timing: {
      'pt-BR': 'Agrupado em um resumo por dia',
      'en-US': 'Bundled into one daily digest',
    },
  },
  {
    id: 'reactions',
    kind: 'reaction',
    minFrequency: 'all',
    title: { 'pt-BR': '12 curtidas e 3 comentários', 'en-US': '12 likes and 3 comments' },
    body: {
      'pt-BR': 'No seu post de corrida de ontem.',
      'en-US': "On yesterday's running post.",
    },
    timing: {
      'pt-BR': 'Agrupado, nunca uma notificação por curtida',
      'en-US': 'Bundled, never one alert per like',
    },
  },
]

/* ---------- Minha Colmeia ---------- */

/** Approved: five visual stages. Thresholds come from `engagementConfig.hive`. */
const hiveStageContent: Omit<HiveStage, 'minBee'>[] = [
  {
    id: 'cell',
    name: { 'pt-BR': 'Primeira célula', 'en-US': 'First cell' },
    description: { 'pt-BR': 'O começo da sua colmeia.', 'en-US': 'Where your hive begins.' },
  },
  {
    id: 'comb',
    name: { 'pt-BR': 'Favo', 'en-US': 'Comb' },
    description: { 'pt-BR': 'Os primeiros hábitos ganham forma.', 'en-US': 'Your first habits take shape.' },
  },
  {
    id: 'young',
    name: { 'pt-BR': 'Colmeia jovem', 'en-US': 'Young hive' },
    description: { 'pt-BR': 'Sua rotina já tem estrutura.', 'en-US': 'Your routine has structure.' },
  },
  {
    id: 'living',
    name: { 'pt-BR': 'Colmeia viva', 'en-US': 'Living hive' },
    description: { 'pt-BR': 'Consistência que se sustenta.', 'en-US': 'Consistency that holds.' },
  },
  {
    id: 'golden',
    name: { 'pt-BR': 'Colmeia dourada', 'en-US': 'Golden hive' },
    description: { 'pt-BR': 'Meses de cuidado construídos.', 'en-US': 'Months of care, built.' },
  },
]

export const hiveStages: HiveStage[] = hiveStageContent.map((stage) => ({
  ...stage,
  minBee: engagementConfig.hive.stageThresholds[stage.id],
}))

/** Bee represented by one filled hive cell (configurable). */
export const BEE_PER_CELL = engagementConfig.hive.beePerCell

export function hiveStageFor(totalBee: number): { stage: HiveStage; next: HiveStage | null } {
  let index = 0
  hiveStages.forEach((stage, i) => {
    if (totalBee >= stage.minBee) index = i
  })
  return { stage: hiveStages[index], next: hiveStages[index + 1] ?? null }
}

export const hiveCollectibles: HiveCollectible[] = [
  {
    id: 'water-comb',
    name: { 'pt-BR': 'Favo da Água', 'en-US': 'Water comb' },
    rule: { 'pt-BR': 'Hidratação concluída em 30 dias', 'en-US': 'Hydration done on 30 days' },
    unlocked: true,
  },
  {
    id: 'dawn-flower',
    name: { 'pt-BR': 'Trilha do Movimento', 'en-US': 'Movement trail' },
    rule: { 'pt-BR': '20 dias com Movimento', 'en-US': '20 days with Movement' },
    unlocked: true,
  },
  {
    id: 'night-lantern',
    name: { 'pt-BR': 'Lanterna do Sono', 'en-US': 'Sleep lantern' },
    rule: { 'pt-BR': 'Sono registrado 14 dias seguidos', 'en-US': 'Sleep logged 14 days in a row' },
    unlocked: false,
  },
  {
    id: 'calm-stone',
    name: { 'pt-BR': 'Pedra da Calma', 'en-US': 'Calm stone' },
    rule: { 'pt-BR': '10 sessões de Mente', 'en-US': '10 Mind sessions' },
    unlocked: true,
  },
  {
    id: 'shared-comb',
    name: { 'pt-BR': 'Favo Compartilhado', 'en-US': 'Shared comb' },
    rule: { 'pt-BR': 'Concluir um desafio de comunidade', 'en-US': 'Finish a community challenge' },
    unlocked: false,
  },
  {
    id: 'crown',
    name: { 'pt-BR': 'Coroa de Mel', 'en-US': 'Honey crown' },
    rule: { 'pt-BR': 'Chegar à Colmeia dourada', 'en-US': 'Reach the Golden hive' },
    unlocked: false,
  },
]

/**
 * Demo chest shown in the prototype. SAMPLE DATA, not a rule: the trigger is the
 * first eligible configured milestone and the amount is the midpoint of the
 * configured Bee range. Real awards are decided by the backend.
 */
export const pendingChest: HiveChestReward | null = (() => {
  const chest = engagementConfig.hiveChest
  const trigger = chest.eligibleTriggers[0]
  if (!trigger) return null
  return {
    trigger,
    bonusBee: Math.round((chest.minBonusBee + chest.maxBonusBee) / 2),
    sample: true,
  }
})()

/* ---------- Community challenges ---------- */

/**
 * Sample content only — titles, targets and dates are not product rules.
 * Weekly, monthly and 12-month challenges share one model (start/end dates + duration).
 */
export const communityChallenges: CommunityChallenge[] = [
  /* Weekly */
  {
    id: 'ch-running-40',
    communityId: 'corrida',
    title: { 'pt-BR': '40 km na semana', 'en-US': '40 km this week' },
    description: {
      'pt-BR': 'Some 40 km de corrida até domingo, no seu ritmo.',
      'en-US': 'Log 40 km of running by Sunday, at your own pace.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'distanceKm',
    target: 40,
    progress: 32,
    participants: 1284,
  },
  {
    id: 'ch-running-consistency',
    communityId: 'corrida',
    title: { 'pt-BR': '5 dias de Daily Check', 'en-US': '5 Daily Check days' },
    description: {
      'pt-BR': 'Consistência antes de volume: 5 check-ins nesta semana.',
      'en-US': 'Consistency before volume: 5 check-ins this week.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'checkDays',
    target: 5,
    progress: 4,
    participants: 2310,
  },
  {
    id: 'ch-strength-4',
    communityId: 'musculacao',
    title: { 'pt-BR': '4 treinos na semana', 'en-US': '4 workouts this week' },
    description: {
      'pt-BR': 'Quatro sessões de força, com descanso entre elas.',
      'en-US': 'Four strength sessions, with rest in between.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'workouts',
    target: 4,
    progress: 2,
    participants: 1876,
  },
  {
    id: 'ch-swim-5',
    communityId: 'natacao',
    title: { 'pt-BR': '5 km de piscina', 'en-US': '5 km in the pool' },
    description: {
      'pt-BR': 'Some 5 km nadando ao longo da semana.',
      'en-US': 'Swim 5 km across the week.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'swimKm',
    target: 5,
    progress: 0,
    participants: 642,
  },
  {
    id: 'ch-mobility-pose',
    communityId: 'mobilidade',
    title: { 'pt-BR': 'Postura da semana: pombo', 'en-US': 'Pose of the week: pigeon' },
    description: {
      'pt-BR': 'Pratique a postura do pombo em 4 sessões curtas de mobilidade.',
      'en-US': 'Practice pigeon pose in 4 short mobility sessions.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'mobilitySessions',
    target: 4,
    progress: 1,
    participants: 918,
    poseGuide: 'pigeon',
  },
  {
    id: 'ch-mind-consistency',
    communityId: 'mindfulness',
    title: { 'pt-BR': '7 dias de Mente', 'en-US': '7 days of Mind' },
    description: {
      'pt-BR': 'Alguns minutos de atenção plena por dia, durante a semana.',
      'en-US': 'A few mindful minutes a day, all week.',
    },
    duration: 'weekly',
    ...thisWeek,
    metric: 'checkDays',
    target: 7,
    progress: 3,
    participants: 1502,
  },

  /* Monthly */
  {
    id: 'ch-strength-12m',
    communityId: 'musculacao',
    title: { 'pt-BR': '12 treinos no mês', 'en-US': '12 workouts this month' },
    description: {
      'pt-BR': 'Três treinos por semana, em média, ao longo do mês.',
      'en-US': 'About three workouts a week, across the month.',
    },
    duration: 'monthly',
    ...thisMonth,
    metric: 'workouts',
    target: 12,
    progress: 5,
    participants: 3204,
  },
  {
    id: 'ch-running-50m',
    communityId: 'corrida',
    title: { 'pt-BR': '50 km de corrida no mês', 'en-US': '50 km of running this month' },
    description: {
      'pt-BR': 'Distribua os quilômetros como preferir até o fim do mês.',
      'en-US': 'Spread the kilometres however you like until month end.',
    },
    duration: 'monthly',
    ...thisMonth,
    metric: 'distanceKm',
    target: 50,
    progress: 18,
    participants: 2876,
  },
  {
    id: 'ch-yoga-8',
    communityId: 'mobilidade',
    title: { 'pt-BR': '8 sessões de yoga', 'en-US': '8 yoga sessions' },
    description: {
      'pt-BR': 'Oito práticas no mês. A postura da árvore tem guia de foto opcional.',
      'en-US': 'Eight practices this month. Tree pose has an optional photo guide.',
    },
    duration: 'monthly',
    ...thisMonth,
    metric: 'yogaSessions',
    target: 8,
    progress: 3,
    participants: 1430,
    poseGuide: 'tree',
  },
  {
    id: 'ch-hydration-20',
    communityId: 'nutricao',
    title: { 'pt-BR': '20 dias de hidratação', 'en-US': '20 days of hydration' },
    description: {
      'pt-BR': 'Alcance sua meta pessoal de água em 20 dias do mês.',
      'en-US': 'Reach your personal water goal on 20 days this month.',
    },
    duration: 'monthly',
    ...thisMonth,
    metric: 'hydrationDays',
    target: 20,
    progress: 0,
    participants: 2051,
  },

  /* 12 months */
  {
    id: 'ch-strength-year',
    communityId: 'musculacao',
    title: { 'pt-BR': '156 treinos no ano', 'en-US': '156 workouts this year' },
    description: {
      'pt-BR': 'Frequência anual de treino, com marcos mensais opcionais de 13 treinos.',
      'en-US': 'Yearly training frequency, with optional monthly checkpoints of 13 workouts.',
    },
    duration: 'annual',
    ...thisYear,
    metric: 'workouts',
    target: 156,
    progress: 112,
    participants: 5120,
    checkpoints: monthlyCheckpoints(13, elapsedSample([13, 12, 14, 13, 9, 13, 15, 11, 12, 0, 0, 0])),
  },
  {
    id: 'ch-running-year',
    communityId: 'corrida',
    title: { 'pt-BR': '1.000 km no ano', 'en-US': '1,000 km this year' },
    description: {
      'pt-BR': 'Distância total de corrida ao longo de 12 meses.',
      'en-US': 'Total running distance across 12 months.',
    },
    duration: 'annual',
    ...thisYear,
    metric: 'distanceKm',
    target: 1000,
    progress: 742,
    participants: 4388,
    checkpoints: monthlyCheckpoints(84, elapsedSample([86, 72, 91, 84, 88, 60, 95, 84, 82, 0, 0, 0])),
  },
  {
    id: 'ch-consistency-year',
    communityId: 'mindfulness',
    title: { 'pt-BR': 'Consistência o ano todo', 'en-US': 'Consistency all year' },
    description: {
      'pt-BR': '250 dias com Daily Check ao longo do ano, no seu ritmo.',
      'en-US': '250 Daily Check days across the year, at your own pace.',
    },
    duration: 'annual',
    ...thisYear,
    metric: 'checkDays',
    target: 250,
    progress: 208,
    participants: 6012,
  },
]

/* ---------- Weekly league (optional, small group) ---------- */

export const leagueMembers: LeagueMember[] = [
  { personId: 'lucas', checkDays: 6 },
  { personId: 'me', checkDays: 5 },
  { personId: 'marina', checkDays: 5 },
  { personId: 'rafael', checkDays: 4 },
  { personId: 'juliana', checkDays: 4 },
  { personId: 'pedro', checkDays: 3 },
  { personId: 'camila', checkDays: 2 },
]

/* ---------- Recap ---------- */

/**
 * SAMPLE generated recaps. In production each recap is generated by the backend and
 * stored with the values and copy used at generation time (versioned), so later
 * configuration changes never rewrite a past recap.
 */
export const recaps: Recap[] = [
  {
    id: 'recap-2026-09',
    period: 'month',
    title: { 'pt-BR': 'Seu setembro', 'en-US': 'Your September' },
    periodLabel: { 'pt-BR': 'Setembro de 2026', 'en-US': 'September 2026' },
    daysInPeriod: 30,
    averageLifeScore: 81,
    longestStreak: 12,
    checkDays: 26,
    habitConsistency: { hydration: 0.92, movement: 0.81, mind: 0.7, nutrition: 0.66, sleep: 0.74 },
    mainActivity: { 'pt-BR': 'Corrida', 'en-US': 'Running' },
    communityPosts: 9,
    challengesDone: 2,
    hiveStartBee: 3240,
    hiveEndBee: 4180,
    socialHighlight: { personId: 'marina', likes: 14, comments: 4 },
    communityContext: [
      { communityId: 'corrida', eligibleMembers: 1840, topPercent: 18 },
      { communityId: 'musculacao', eligibleMembers: 1210, topPercent: 26 },
      { communityId: 'mindfulness', eligibleMembers: 31, topPercent: 0 },
    ],
    highlights: [
      { label: { 'pt-BR': 'Novo recorde', 'en-US': 'New record' }, value: { 'pt-BR': 'PR 10 km em 48:12', 'en-US': '10 km PR in 48:12' } },
      { label: { 'pt-BR': 'Medalha', 'en-US': 'Badge' }, value: { 'pt-BR': 'Semana de Ferro', 'en-US': 'Iron Week' } },
      { label: { 'pt-BR': 'Dias perfeitos', 'en-US': 'Perfect days' }, value: { 'pt-BR': '7 dias com 100', 'en-US': '7 days at 100' } },
    ],
  },
  {
    id: 'recap-2026',
    period: 'year',
    title: { 'pt-BR': 'Seu 2026', 'en-US': 'Your 2026' },
    periodLabel: { 'pt-BR': '2026', 'en-US': '2026' },
    daysInPeriod: 365,
    averageLifeScore: 76,
    longestStreak: 23,
    checkDays: 214,
    habitConsistency: { hydration: 0.83, movement: 0.82, mind: 0.61, nutrition: 0.58, sleep: 0.69 },
    mainActivity: { 'pt-BR': 'Corrida', 'en-US': 'Running' },
    communityPosts: 64,
    challengesDone: 11,
    hiveStartBee: 620,
    hiveEndBee: 4180,
    socialHighlight: { personId: 'lucas', likes: 96, comments: 31 },
    communityContext: [
      { communityId: 'corrida', eligibleMembers: 6120, topPercent: 22 },
      { communityId: 'musculacao', eligibleMembers: 4380, topPercent: 31 },
      { communityId: 'mindfulness', eligibleMembers: 44, topPercent: 0 },
    ],
    highlights: [
      { label: { 'pt-BR': 'Primeira meia', 'en-US': 'First half' }, value: { 'pt-BR': '21 km em 1:52:40', 'en-US': '21 km in 1:52:40' } },
      { label: { 'pt-BR': 'Disciplina Construída', 'en-US': 'Built Discipline' }, value: { 'pt-BR': 'Alcançada em maio', 'en-US': 'Reached in May' } },
      { label: { 'pt-BR': 'Colmeia', 'en-US': 'Hive' }, value: { 'pt-BR': 'Colmeia viva', 'en-US': 'Living hive' } },
    ],
  },
]

/* ---------- Progress photo timeline (private by default) ---------- */

export const progressPhotos: ProgressPhoto[] = [
  {
    id: 'pp-3',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/3e21dbf1-e4d7-4f2b-a60e-5f4436f1e50c.jpg',
    date: '2026-09-21',
    note: { 'pt-BR': 'Depois do bloco de força', 'en-US': 'After the strength block' },
  },
  {
    id: 'pp-2',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/e5c64db6-5638-4bd6-adb3-5668f26323d4.jpg',
    date: '2026-07-14',
    note: { 'pt-BR': 'Meio do ciclo de corrida', 'en-US': 'Mid running cycle' },
  },
  {
    id: 'pp-1',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/0d4ee639-0b8b-4c4e-8683-ea9eb42b939a.jpg',
    date: '2026-05-02',
    note: { 'pt-BR': 'Ponto de partida', 'en-US': 'Starting point' },
  },
]

/* ---------- Future automatic habit input (not active) ---------- */

export const autoInputSources: AutoInputSource[] = [
  {
    habit: 'movement',
    providers: ['Apple Health', 'Google Health Connect'],
    signal: { 'pt-BR': 'Treinos e minutos ativos', 'en-US': 'Workouts and active minutes' },
  },
  {
    habit: 'sleep',
    providers: ['Apple Health', 'Google Health Connect'],
    signal: { 'pt-BR': 'Duração do sono', 'en-US': 'Sleep duration' },
  },
  {
    habit: 'hydration',
    providers: ['Apple Health', 'Google Health Connect'],
    signal: { 'pt-BR': 'Água registrada', 'en-US': 'Water logged' },
  },
  {
    habit: 'mindfulness',
    providers: ['Apple Health'],
    signal: { 'pt-BR': 'Minutos de atenção plena', 'en-US': 'Mindful minutes' },
  },
]
